import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  FormControl,
  FormLabel,
  Input,
  Select,
  Alert,
  AlertIcon,
  useToast,
  Card,
  CardBody,
  SimpleGrid,
  Badge,
  Icon,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FaSignOutAlt,
  FaCloud,
  FaFileAlt,
  FaBolt,
  // FaBrain, // Copilot temporarily hidden
  FaDollarSign,
  FaChartLine,
  FaClock,
  FaShieldAlt,
  FaCheckCircle,
  FaIndustry,
  FaRocket,
  FaPlane,
  FaCar,
  FaLeaf,
  FaWrench,
  FaAnchor,
  FaOilCan,
  FaChevronDown,
  FaChevronUp,
  FaEnvelope,
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import Turnstile from 'react-turnstile';

import { TURNSTILE_SITE_KEY } from '../config';
import { useAuth } from '../context/AuthContext';
import NavDropdowns from './NavDropdowns';
import Footer from './Footer';
import examplesApiService from '../services/examplesApi';

/** Values must match backend `_INDUSTRY_BRIEF_PDF_BY_USE_CASE` keys. */
const USE_CASE_OPTIONS = [
  { value: 'aerospace', label: 'Aerospace' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'energy', label: 'Energy' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'marine', label: 'Marine' },
  { value: 'oil_and_gas', label: 'Oil & gas' },
];

const INDUSTRY_BRIEFS = [
  {
    value: 'aerospace',
    label: 'Aerospace',
    icon: FaPlane,
    color: 'blue',
    gradient: 'linear-gradient(135deg, #0891b2, #155e75)',
    tagline: '5 validated templates · vortex transport to turbulent aerofoil wakes',
    highlights: ['Shock capturing, boundary layers, and turbulent wakes on GPUs', 'Browser-based, same-day runs — no meshing suite or HPC procurement', 'High-order accuracy verified on Euler-vortex and Taylor–Green benchmarks'],
    summary: `SRS brings the high-order, GPU-accelerated PyFR engine — a Flux Reconstruction solver for compressible and scale-resolving flow — to the aerospace engineer's browser, without local installs, mesh-generation tools, or HPC procurement. The documented aerospace templates span the external-aerodynamics stack: the 2D Euler vortex for high-order accuracy verification, the 2D double Mach reflection and 2D viscous shock tube for shock capturing and shock–boundary-layer interaction, the 3D Taylor–Green vortex for transition to turbulence, and the 3D triangular aerofoil for turbulent-inflow wake and force statistics.

 Each template launches in minutes, is parameterized for a specific geometry and flight condition, and is post-processed in a unified workspace. Domain experts spend their time on design decisions rather than solver setup.

For aerospace OEMs, suppliers, and research teams, the result is measurable: weeks-long meshing and HPC setup collapse into same-day, high-fidelity iterations on aerofoil, shock-dominated, and turbulent-wake workloads, and cross-functional teams collaborate on shared cases without specialized CFD tooling.`,
  },
  {
    value: 'automotive',
    label: 'Automotive',
    icon: FaCar,
    color: 'red',
    gradient: 'linear-gradient(135deg, #C53030, #E53E3E)',
    tagline: '4 validated templates · bluff-body wakes, boundary layers, turbulence',
    highlights: ['Predict vortex shedding, wake drag, and aeroacoustic tones', 'Scale-resolving external aero without CPU-era wall times', 'From a browser — no local CFD install or HPC procurement'],
    summary: `SRS brings the high-order, GPU-accelerated PyFR engine — a solver for scale-resolving turbulent and bluff-body flow — to automotive aerodynamics and thermal teams, from any browser and without HPC procurement. The documented automotive templates target the physics that drive drag, cooling, and wind noise: the 2D incompressible cylinder for bluff-body vortex shedding behind mirrors, antennas, and underbody members; the 2D Couette flow for wall-bounded shear and boundary layers; the 3D Taylor–Green vortex for turbulence; and the 3D triangular aerofoil for 3D external aerodynamics with turbulent inflow.

High-order accuracy captures the Strouhal number, unsteady lift and drag, and wake deficits that govern aeroacoustic tones and range/economy.

For automotive OEMs and tier-1 suppliers, the result is direct: simulation-led iteration replaces repeated wind-tunnel and NVH prototype cycles, and turbulent external-aero studies that once needed an HPC queue run on-demand on enterprise GPUs.`,
  },
  {
    value: 'energy',
    label: 'Energy',
    icon: FaLeaf,
    color: 'green',
    gradient: 'linear-gradient(135deg, #276749, #48BB78)',
    tagline: '7 validated templates · turbulence, shocks, VIV, wall-bounded flow',
    highlights: ['Predict vortex-induced vibration on tubes, stacks, and conductors', 'Resolve compressible shocks and turbulent flow on enterprise GPUs', 'Every documented benchmark maps to an energy asset'],
    summary: `SRS brings the high-order, GPU-accelerated PyFR engine — a solver for compressible, incompressible, and scale-resolving flow — to engineers across the energy value chain, in the browser and without HPC procurement. Every documented benchmark maps to an energy asset: the 2D incompressible cylinder for vortex-induced vibration of heat-exchanger tubes, stacks, and conductors; the 3D Taylor–Green vortex and 3D triangular aerofoil for turbulent turbine and wind-energy flows; the 2D double Mach reflection and 2D viscous shock tube for compressible, shock-containing transients; the 2D Couette flow for wall-bounded shear; and the 2D Euler vortex for accuracy verification.

Scale-resolving prediction of shedding frequency, unsteady loading, and turbulent mixing lets teams anticipate flow-induced vibration and losses before they cause failures.

For energy operators, OEMs, and research labs, the result is direct: high-fidelity, auditable simulation replaces conservative rules of thumb and month-long physical test campaigns, and every documented case runs on-demand without local installs or HPC capex.`,
  },
  {
    value: 'manufacturing',
    label: 'Manufacturing',
    icon: FaWrench,
    color: 'purple',
    gradient: 'linear-gradient(135deg, #553C9A, #805AD5)',
    tagline: '2 validated templates · wall-bounded shear & compressible shock flow',
    highlights: ['Wall-bounded shear and pressure-loss physics via Couette flow', 'Compressible, shock-containing internal flow via the viscous shock tube', 'GPU-accelerated, browser-based, AI-assisted case setup'],
    summary: `SRS brings the high-order, GPU-accelerated PyFR engine to manufacturing engineering teams from any browser, with AI-assisted case setup and no meshing or HPC bottleneck. The documented manufacturing templates cover two foundational regimes: the 2D Couette flow for wall-bounded viscous shear — the physics behind coating, lubrication, and narrow-gap passages — and the 2D viscous shock tube for compressible, shock-driven internal flow relevant to forming, pressure relief, and rapid-transient processes.

Built on the open PyFR engine — a peer-reviewed, high-order Flux Reconstruction code for scale-resolving simulation of turbulent flows on unstructured meshes — SRS delivers deterministic solutions to the governing flow equations that engineering and process reviewers can audit parameter by parameter.

SRS shortens the path from an engineering question to a defensible, simulation-led answer and reduces the CFD-specialist queue that gates analysis today.`,
  },
  {
    value: 'marine',
    label: 'Marine',
    icon: FaAnchor,
    color: 'teal',
    gradient: 'linear-gradient(135deg, #2C7A7B, #38B2AC)',
    tagline: '1 validated template · bluff-body vortex shedding & VIV',
    highlights: ['Predict riser and mooring VIV from shedding frequency and unsteady lift', 'Right-size strakes and fairings before at-sea trials', 'High-order incompressible wake dynamics on GPUs'],
    summary: `SRS brings the high-order, GPU-accelerated PyFR engine — a solver for scale-resolving incompressible flow — to marine and offshore engineers in the browser, without local CFD installs or HPC procurement. The documented marine template is the 2D incompressible cylinder: the canonical von Kármán vortex street that governs vortex-induced vibration (VIV) of risers, mooring lines, subsea pipelines, and hull appendages.

High-order accuracy captures the shedding frequency and unsteady lift that feed VIV fatigue-life models directly, letting teams right-size strakes and fairings and bound appendage drag before expensive at-sea trials.

The same template extends naturally to any bluff-body geometry — cylinders, risers, cables, and tubes — that an engineer swaps in.`,
  },
  {
    value: 'oil_and_gas',
    label: 'Oil & gas',
    icon: FaOilCan,
    color: 'yellow',
    gradient: 'linear-gradient(135deg, #B7791F, #D69E2E)',
    tagline: '1 validated template · shock reflection & compressible blast flow',
    highlights: ['Model shock reflection and compressible blowdown/blast physics', 'High-order shock capturing on unstructured GPU meshes', 'Browser-based runs with AI-assisted configuration'],
    summary: `SRS brings the high-order, GPU-accelerated PyFR engine — a compressible-flow solver — to oil & gas engineers in the browser, without local installs or HPC procurement. The documented template is the 2D double Mach reflection: a strong-shock reflection benchmark that exercises the shock-capturing physics behind blowdown, pressure-relief, and blast-type compressible transients on unstructured meshes.

High-order shock capturing resolves reflected and interacting shocks that lower-order tools smear, giving a defensible view of peak pressures and wave paths.

Teams can evaluate compressible-transient scenarios before committing hardware.`,
  },
];

const CHALLENGES = [
  { challenge: 'Physical prototype cycles (3–6 per product)', cost: '$500K – $5M per product line' },
  { challenge: 'Late-stage design failures caught in testing', cost: '$5M – $50M per finding' },
  { challenge: 'CFD specialist bottleneck (2–3 engineers gate all analysis)', cost: '4–8 week queue per request' },
  { challenge: 'Legacy CFD licenses + HPC infrastructure', cost: '$200K – $1M/year per team' },
  { challenge: 'Meshing and geometry cleanup', cost: '60–80% of total simulation time' },
  { challenge: 'Regulatory compliance delays', cost: '$10K – $100K/day in penalties' },
];

const CAPABILITIES = [
  { icon: FaBolt, color: 'orange', title: 'GPU-Accelerated Solvers', desc: '10–100× faster than CPU-based tools. Results in hours, not weeks.' },
  // { icon: FaBrain, color: 'purple', title: 'AI Copilot', desc: 'Engineers describe needs in plain English. Copilot drafts a candidate PyFR configuration file for review.' },
  { icon: FaCheckCircle, color: 'green', title: 'High-Order on Unstructured Meshes', desc: 'PyFR handles mixed elements and unstructured meshes for complex geometries. Eliminates the body-fitted meshing bottleneck.' },
  { icon: FaIndustry, color: 'blue', title: 'Validated Templates', desc: 'Fork and modify proven configurations for your industry. No starting from scratch.' },
  { icon: FaCloud, color: 'brand', title: 'Cloud Infrastructure', desc: 'NVIDIA A100/H100 GPUs, auto-scaling. No HPC procurement, no IT overhead.' },
  { icon: FaDollarSign, color: 'teal', title: 'Usage-Based Pricing', desc: 'Pay per compute hour from €1. No seat licenses. Finance pays for what was actually used.' },
];

const WHY_NOW = [
  { icon: FaBolt, color: 'orange', title: 'GPU Maturity', desc: 'NVIDIA A100 and H100 deliver 10–100× the throughput of a 32-core CPU node for the dense linear algebra at the heart of CFD — at a fraction of the per-job cost when consumed on-demand.' },
  // { icon: FaBrain, color: 'purple', title: 'AI Copilots', desc: 'LLMs trained on the PyFR documentation can draft initial configuration files from natural language, lowering the interface barrier that previously gated CFD to 2–3 specialists per organisation.' },
  { icon: FaChartLine, color: 'green', title: 'Cloud Economics', desc: 'Pay-per-GPU-hour eliminates the $50K–$500K HPC capex cycle. Engineering teams self-provision; finance pays for what was actually used.' },
];

const IndustryCard = ({ brief, onSelect }) => {
  const { isOpen, onToggle } = useDisclosure();
  return (
    <Card shadow="sm" borderWidth="1px" borderColor="gray.200" overflow="hidden" h="full">
      <Box bg={brief.gradient} px={5} py={4}>
        <HStack spacing={3}>
          <Icon as={brief.icon} boxSize={7} color="white" />
          <VStack align="start" spacing={0}>
            <Heading size="sm" color="white">{brief.label}</Heading>
            <Text fontSize="xs" color="whiteAlpha.800">{brief.tagline}</Text>
          </VStack>
        </HStack>
      </Box>
      <CardBody>
        <VStack align="start" spacing={3}>
          {brief.highlights.map((h) => (
            <HStack key={h} spacing={2} align="start">
              <Icon as={FaCheckCircle} color={`${brief.color}.400`} mt="2px" flexShrink={0} />
              <Text fontSize="sm" color="gray.700">{h}</Text>
            </HStack>
          ))}
          <Button size="xs" variant="ghost" rightIcon={isOpen ? <FaChevronUp /> : <FaChevronDown />} onClick={onToggle} px={0} color="gray.500">
            {isOpen ? 'Less' : 'Full summary'}
          </Button>
          <Collapse in={isOpen} animateOpacity>
            <Text fontSize="sm" color="gray.600" whiteSpace="pre-line">{brief.summary}</Text>
          </Collapse>
          <Button
            size="sm"
            colorScheme={brief.color}
            leftIcon={<FaEnvelope />}
            variant="outline"
            w="full"
            onClick={() => onSelect(brief.value)}
          >
            Request this brief
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
};

const ExecutiveBrief = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef(null);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [useCase, setUseCase] = useState('manufacturing');

  useEffect(() => {
    if (user) {
      const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
      if (name) setFullName(name);
      if (user.email) setEmail(user.email);
      if (user.organization) setCompany(user.organization);
    }
  }, [user]);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetTurnstile = () => {
    if (typeof window !== 'undefined' && window.turnstile?.reset) {
      try { window.turnstile.reset(); } catch { /* noop */ }
    }
    setTurnstileToken('');
  };

  const selectIndustry = (value) => {
    setUseCase(value);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fn = fullName.trim();
    const em = email.trim();
    const co = company.trim();
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
    if (!fn || !em || !co || !useCase || !turnstileToken) {
      toast({ title: 'Missing fields', description: 'Please fill in all required fields, select an industry, and complete verification.', status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    if (!looksLikeEmail) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email address.', status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    setIsSubmitting(true);
    try {
      await examplesApiService.requestExecutiveBrief({
        full_name: fn, email: em, company: co, use_case: useCase,
        turnstile_token: turnstileToken,
      });
      toast({ title: 'Request received', description: 'We will email the executive brief to you shortly.', status: 'success', duration: 6000, isClosable: true });
      if (!user) {
        setFullName(''); setEmail(''); setCompany('');
      }
      setUseCase('manufacturing');
      resetTurnstile();
    } catch (err) {
      toast({ title: 'Request failed', description: err.message || 'Something went wrong. Please try again.', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="gray.50">
      <Helmet>
        <title>Executive industry brief | SRS</title>
        <meta name="description" content="Request a SRS industry executive brief PDF by email. Select your sector and we will send the relevant overview." />
        <link rel="canonical" href="https://srs.numericalai.net/executive-brief" />
      </Helmet>

      {/* Nav */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">
            <HStack spacing={1} cursor="pointer" onClick={() => navigate('/')} _hover={{ opacity: 0.8 }} transition="opacity 0.2s" borderRadius="md" p={2} _active={{ transform: 'scale(0.98)' }}>
              <Image src="/simple-logo.png" alt="SRS Logo" boxSize={14} objectFit="contain" />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">AI-Enhanced Simulations at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              {user ? (
                <Button leftIcon={<FaCloud />} colorScheme="brand" size="sm" variant="ghost" as={RouterLink} to="/dashboard">Dashboard</Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">Sign In</Button>
              )}
              {user && (
                <Button leftIcon={<FaSignOutAlt />} variant="outline" size="sm" onClick={() => logout()}>Sign Out</Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Hero */}
      <Box bg="gray.800" py={{ base: 14, md: 20 }}>
        <Container maxW="container.lg" textAlign="center">
          <VStack spacing={5}>
            <Badge colorScheme="brand" variant="solid" fontSize="sm" px={4} py={1} borderRadius="full" textTransform="none">
              Confidential — For Executive Review
            </Badge>
            <Heading as="h1" size="2xl" color="white" lineHeight="shorter">Industry Executive Briefs</Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Sector-focused business cases for deploying GPU-accelerated, AI-assisted CFD simulation —
              prepared for engineering leadership and C-suite stakeholders.
            </Text>
            {/* Copilot quote temporarily hidden
            <Text fontStyle="italic" color="gray.400" fontSize="md" borderLeft="3px solid" borderColor="brand.400" pl={4} textAlign="left" maxW="xl">
              "SRS is to CFD what GitHub Copilot is to software development."
            </Text>
            */}
            <Button colorScheme="brand" size="lg" leftIcon={<FaFileAlt />} onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
              Request a brief by email
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Key outcomes strip */}
      <Box bg="brand.600" py={6}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={4}>
            {[
              { icon: FaClock, label: 'Same-day or few-day simulation turnarounds' },
              { icon: FaShieldAlt, label: 'Eliminate meshing and HPC procurement bottlenecks' },
              { icon: FaDollarSign, label: '$10M+ annual savings per programme — from €1' },
            ].map(({ icon, label }) => (
              <HStack key={label} spacing={3} color="white">
                <Icon as={icon} boxSize={6} flexShrink={0} />
                <Text fontWeight="semibold" fontSize="sm">{label}</Text>
              </HStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <Box flex="1">

        {/* Industry briefs grid */}
        <Box bg="white" py={{ base: 12, md: 16 }}>
          <Container maxW="container.xl">
            <VStack spacing={10} align="stretch">
              <VStack spacing={3} textAlign="center">
                <Heading size="xl" color="gray.800">Available Industry Briefs</Heading>
                <Text color="gray.600" maxW="2xl" mx="auto">
                  Six sector-specific PDFs, each covering industry cost benchmarks, ROI projections, relevant
                  physics templates, and a suggested 90-day adoption roadmap. Click "Request this brief" on any
                  card to pre-fill the form below.
                </Text>
              </VStack>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                {INDUSTRY_BRIEFS.map((brief) => (
                  <IndustryCard key={brief.value} brief={brief} onSelect={selectIndustry} />
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        <Divider />

        <Divider />

        {/* Request form */}
        <Box bg="white" py={{ base: 12, md: 16 }} ref={formRef}>
          <Container maxW="container.xl">
            <VStack spacing={8} align="stretch">
              <VStack spacing={3} textAlign="center">
                <Icon as={FaRocket} color="brand.500" boxSize={8} />
                <Heading size="lg" color="gray.800">Request your industry brief</Heading>
                <Text color="gray.600" maxW="xl" mx="auto">
                  Select your sector and we will email the full PDF — including industry-specific cost benchmarks,
                  ROI projections, and a suggested 90-day adoption roadmap.
                </Text>
              </VStack>

              {/* Two-column layout: summary preview + form */}
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} alignItems="start">

                {/* Left: industry summary preview */}
                {(() => {
                  const selected = INDUSTRY_BRIEFS.find((b) => b.value === useCase);
                  if (!selected) {
                    return (
                      <Box
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="lg"
                        p={8}
                        bg="gray.50"
                        display="flex"
                        flexDir="column"
                        alignItems="center"
                        justifyContent="center"
                        minH="360px"
                        textAlign="center"
                        gap={4}
                      >
                        <Icon as={FaFileAlt} boxSize={10} color="gray.300" />
                        <Text color="gray.400" fontSize="md">
                          Select an industry on the right to preview the executive summary here.
                        </Text>
                      </Box>
                    );
                  }
                  return (
                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                      <Box bg={selected.gradient} px={6} py={5}>
                        <HStack spacing={3}>
                          <Icon as={selected.icon} boxSize={8} color="white" />
                          <VStack align="start" spacing={0}>
                            <Heading size="md" color="white">{selected.label}</Heading>
                            <Text fontSize="sm" color="whiteAlpha.800">{selected.tagline}</Text>
                          </VStack>
                        </HStack>
                      </Box>
                      <Box bg="white" p={6}>
                        <VStack align="start" spacing={3} mb={5}>
                          {selected.highlights.map((h) => (
                            <HStack key={h} spacing={2} align="start">
                              <Icon as={FaCheckCircle} color={`${selected.color}.400`} mt="2px" flexShrink={0} />
                              <Text fontSize="sm" color="gray.700" fontWeight="medium">{h}</Text>
                            </HStack>
                          ))}
                        </VStack>
                        <Divider mb={4} />
                        <Text fontSize="sm" color="gray.600" whiteSpace="pre-line" lineHeight="1.8">
                          {selected.summary}
                        </Text>
                        <Divider mt={5} mb={3} />
                        <Text fontSize="sm" color="gray.500" fontStyle="italic">
                          Request the full industry brief PDF via the form on the right — it will be emailed to you directly.
                        </Text>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Right: form */}
                <Card shadow="md" borderWidth="1px" borderColor="gray.200">
                  <CardBody p={{ base: 6, md: 8 }}>
                    <form onSubmit={handleSubmit}>
                      <VStack spacing={4} align="stretch">
                        <Alert status="info" borderRadius="md" fontSize="sm">
                          <AlertIcon />
                          The PDF will be sent to the email you provide below. Use your professional email when possible.
                        </Alert>

                        <FormControl isRequired>
                          <FormLabel>Full name</FormLabel>
                          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Director" size="md" />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel>Email</FormLabel>
                          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" size="md" />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel>Company / organization</FormLabel>
                          <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company or institution" size="md" />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel>Industry</FormLabel>
                          <Select placeholder="Select your industry" value={useCase} onChange={(e) => setUseCase(e.target.value)} size="md" bg="white">
                            {USE_CASE_OPTIONS.map(({ value, label }) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </Select>
                        </FormControl>

                        <Box>
                          <Text fontSize="sm" color="gray.600" mb={2}>Verification</Text>
                          <Turnstile sitekey={TURNSTILE_SITE_KEY} onVerify={(token) => setTurnstileToken(token)} onExpire={() => setTurnstileToken('')} />
                        </Box>

                        <Button type="submit" colorScheme="brand" leftIcon={<FaFileAlt />} isLoading={isSubmitting} loadingText="Submitting…" isDisabled={!turnstileToken}>
                          Email me the executive brief
                        </Button>
                      </VStack>
                    </form>
                  </CardBody>
                </Card>

              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

      </Box>

      <Footer />
    </Box>
  );
};

export default ExecutiveBrief;
